import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { removeTimestampPrefix } from '@/utils/file-utils';

interface CitationTooltipProps {
  fileName?: string;
  pageNumber?: number;
  position?: string;
  sourceLabel?: string;
  targetElement: HTMLElement;
  url?: string;
}

export const CitationTooltip: React.FC<CitationTooltipProps> = ({
  fileName,
  pageNumber,
  position,
  sourceLabel,
  targetElement,
  url,
}) => {
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 0;

      // Position tooltip above the citation, centered above the citation
      setCoords({
        x: rect.left + rect.width / 2,
        y: rect.top - tooltipHeight - 8,
      });

      // Once we have dimensions and position, make it visible
      if (tooltipHeight > 0 && !isVisible) {
        setIsVisible(true);
      }
    };

    // Initial position calculation
    updatePosition();

    // Use requestAnimationFrame to ensure DOM has rendered and we have dimensions
    const rafId = requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [targetElement, isVisible]);

  if (!coords) return null;

  const truncateFilename = (name: string, maxLength: number = 25) => {
    const cleanName = removeTimestampPrefix(name);
    if (cleanName.length <= maxLength) return cleanName;
    return `${cleanName.substring(0, maxLength - 3).trimEnd()}...`;
  };

  return createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-50 pointer-events-none transition-opacity duration-100"
      style={{
        left: `${coords.x}px`,
        top: `${coords.y}px`,
        transform: 'translateX(-50%)',
        opacity: isVisible ? 1 : 0,
      }}
    >
      <div className="bg-gray-800/95 backdrop-blur-xs text-white text-xs rounded-md shadow-xl px-2.5 py-1.5 max-w-xs">
        {sourceLabel && (
          <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-white/55">
            {sourceLabel}
          </div>
        )}
        {fileName && (
          <div className="font-medium text-white/90 mb-0.5 text-[11px]">
            {url ? fileName : truncateFilename(fileName)}
          </div>
        )}
        {url && (
          <div className="max-w-[240px] break-all text-white/70 text-[10px]">
            {url}
          </div>
        )}

        {/* Page info - only if available */}
        {pageNumber && (
          <div className="flex items-center gap-1.5 text-white/70 text-[10px]">
            <span className="flex items-center gap-0.5">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Page {pageNumber}
            </span>
          </div>
        )}

        {position && (
          <div className="max-w-[220px] truncate text-white/70 text-[10px]">
            {position}
          </div>
        )}

        {/* Tooltip arrow */}
        <div className="absolute left-1/2 top-full -translate-x-1/2 -mt-px">
          <div className="border-[5px] border-transparent border-t-gray-800/95" />
        </div>
      </div>
    </div>,
    document.body
  );
};

interface CitationTooltipManagerProps {
  children: React.ReactNode;
}

export const CitationTooltipManager: React.FC<CitationTooltipManagerProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    fileName?: string;
    pageNumber?: number;
    position?: string;
    sourceLabel?: string;
    targetElement: HTMLElement;
    url?: string;
  } | null>(null);

  useEffect(() => {
    const handleMouseEnter = (e: MouseEvent) => {
      const eventTarget = e.target;
      const container = containerRef.current;
      if (!(eventTarget instanceof Element) || !container?.contains(eventTarget)) return;

      const target = eventTarget.closest<HTMLElement>('.citation-link, .citation-deleted');
      if (!target || !container.contains(target)) return;

      if (target.classList.contains('citation-deleted')) {
        setTooltip({
          fileName: 'Document deleted',
          targetElement: target,
        });
      } else if (target.classList.contains('citation-link')) {
        const webUrl = target.getAttribute('data-web-url');
        if (webUrl) {
          setTooltip({
            fileName: target.getAttribute('data-file-name') || webUrl,
            sourceLabel: target.getAttribute('data-site-name') || 'Public web',
            targetElement: target,
            url: webUrl,
          });
          return;
        }

        const fileName = target.getAttribute('data-file-name');
        const pageNumber = target.getAttribute('data-page-number');
        const position = target.getAttribute('data-position');

        // Show tooltip if we have fileName (with or without page number)
        if (fileName) {
          setTooltip({
            fileName: fileName || undefined,
            pageNumber: pageNumber ? parseInt(pageNumber) : undefined,
            position: position || undefined,
            targetElement: target,
          });
        }
      }
    };

    const handleMouseLeave = (e: MouseEvent) => {
      const eventTarget = e.target;
      const container = containerRef.current;
      if (!(eventTarget instanceof Element) || !container?.contains(eventTarget)) return;

      const target = eventTarget.closest<HTMLElement>('.citation-link, .citation-deleted');
      if (!target || !container.contains(target)) return;

      if (target.classList.contains('citation-link') || target.classList.contains('citation-deleted')) {
        setTooltip(null);
      }
    };

    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);

    return () => {
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
    };
  }, []);

  return (
    <>
      <div ref={containerRef} className="contents">
        {children}
      </div>
      {tooltip && (
        <CitationTooltip
          fileName={tooltip.fileName}
          pageNumber={tooltip.pageNumber}
          position={tooltip.position}
          sourceLabel={tooltip.sourceLabel}
          targetElement={tooltip.targetElement}
          url={tooltip.url}
        />
      )}
    </>
  );
};
